import React from 'react';
import { ArrowDown, ArrowRight, Circle, Square, Hexagon } from 'lucide-react';

/**
 * DiagramRenderer - Renders visual diagrams from text descriptions
 * Supports: boxes, arrows, layers, nodes, connections
 */
const DiagramRenderer = ({ description, title }) => {
  const lines = description.split('\n').filter(l => l.trim());
  
  // Parse diagram structure
  const elements = lines.map((line, idx) => {
    const trimmed = line.trim();
    const lower = trimmed.toLowerCase();
    
    // Detect element type
    if (lower.includes('input layer') || lower.includes('input (')) {
      return { type: 'layer', text: trimmed, color: 'blue', position: 'top' };
    } else if (lower.includes('hidden layer') || lower.includes('middle layer')) {
      return { type: 'layer', text: trimmed, color: 'purple', position: 'middle' };
    } else if (lower.includes('output layer') || lower.includes('output (')) {
      return { type: 'layer', text: trimmed, color: 'green', position: 'bottom' };
    } else if (lower.includes('weight') || lower.includes('(w')) {
      return { type: 'connection', text: trimmed, style: 'dashed' };
    } else if (lower.includes('arrow') || lower.includes('→') || lower.includes('->') || lower.includes('connects')) {
      return { type: 'arrow', direction: 'down' };
    } else if (lower.match(/\((\d+)\s*(node|neuron|unit)/)) {
      const match = lower.match(/\((\d+)\s*(node|neuron|unit)/);
      return { type: 'nodes', count: parseInt(match[1]), text: trimmed };
    } else if (trimmed.startsWith('-') || trimmed.startsWith('•')) {
      return { type: 'bullet', text: trimmed.replace(/^[-•]\s*/, '') };
    } else if (trimmed) {
      return { type: 'text', text: trimmed };
    }
    return null;
  }).filter(Boolean);
  
  // Render based on diagram type
  const hasLayers = elements.some(e => e.type === 'layer');
  
  if (hasLayers) {
    return <LayeredDiagram elements={elements} title={title} />;
  } else {
    return <FlowDiagram elements={elements} title={title} />;
  }
};

// Layered diagram (for neural networks, architectures)
const LayeredDiagram = ({ elements, title }) => {
  const layers = elements.filter(e => e.type === 'layer');
  const others = elements.filter(e => e.type !== 'layer');
  
  const colorMap = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600'
  };
  
  return (
    <div className="w-full">
      {/* Layers with visual nodes */}
      <div className="flex flex-col items-center gap-6 py-6">
        {layers.map((layer, idx) => {
          // Extract node count if mentioned
          const nodeMatch = layer.text.match(/\(([^)]+)\)/);
          const nodeInfo = nodeMatch ? nodeMatch[1] : '';
          const layerName = layer.text.replace(/\([^)]+\)/, '').trim();
          
          return (
            <React.Fragment key={idx}>
              {/* Layer Box */}
              <div className="flex flex-col items-center gap-3 w-full max-w-2xl">
                {/* Layer Label */}
                <div className={`px-6 py-3 rounded-xl bg-gradient-to-r ${colorMap[layer.color]} text-white font-semibold text-center shadow-lg min-w-[300px]`}>
                  {layerName}
                </div>
                
                {/* Visual Nodes */}
                {nodeInfo && (
                  <div className="flex items-center justify-center gap-3 flex-wrap">
                    {Array.from({ length: Math.min(parseInt(nodeInfo) || 3, 8) }).map((_, i) => (
                      <div key={i} className={`w-12 h-12 rounded-full bg-gradient-to-br ${colorMap[layer.color]} shadow-md flex items-center justify-center text-white text-xs font-bold border-2 border-white`}>
                        {i + 1}
                      </div>
                    ))}
                    {parseInt(nodeInfo) > 8 && (
                      <div className="text-slate-500 font-semibold">...</div>
                    )}
                  </div>
                )}
                
                {/* Node info text */}
                {nodeInfo && (
                  <div className="text-xs text-slate-500 font-medium">
                    ({nodeInfo})
                  </div>
                )}
              </div>
              
              {/* Arrow between layers */}
              {idx < layers.length - 1 && (
                <div className="flex flex-col items-center">
                  <ArrowDown className="text-slate-400" size={32} strokeWidth={3} />
                  {/* Connection label if any */}
                  {others.find(e => e.type === 'connection') && idx === 0 && (
                    <div className="text-xs text-slate-500 mt-1 px-3 py-1 bg-slate-100 rounded-full">
                      {others.find(e => e.type === 'connection').text}
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Additional info */}
      {others.filter(e => e.type === 'bullet' || e.type === 'text').length > 0 && (
        <div className="mt-6 space-y-2 bg-slate-50 rounded-xl p-4">
          {others.filter(e => e.type === 'bullet' || e.type === 'text').map((item, idx) => (
            <div key={idx} className="flex items-start gap-2">
              {item.type === 'bullet' && <div className="w-2 h-2 rounded-full bg-violet-500 mt-2"></div>}
              <p className="text-slate-700 text-sm">{item.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Flow diagram (for processes, steps)
const FlowDiagram = ({ elements, title }) => {
  return (
    <div className="w-full">
      <div className="flex flex-col items-center gap-4 py-6">
        {elements.map((element, idx) => {
          if (element.type === 'arrow') {
            return (
              <div key={idx} className="flex items-center justify-center">
                <ArrowDown className="text-violet-500" size={28} strokeWidth={3} />
              </div>
            );
          } else if (element.type === 'nodes') {
            return (
              <div key={idx} className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-3">
                  {Array.from({ length: Math.min(element.count, 6) }).map((_, i) => (
                    <div key={i} className="w-14 h-14 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg flex items-center justify-center text-white font-bold border-2 border-white">
                      {i + 1}
                    </div>
                  ))}
                  {element.count > 6 && <span className="text-slate-500 font-bold">...</span>}
                </div>
                <p className="text-sm text-slate-600">{element.text}</p>
              </div>
            );
          } else if (element.type === 'bullet') {
            return (
              <div key={idx} className="flex items-start gap-3 w-full max-w-xl bg-white rounded-lg px-4 py-3 shadow-sm border-l-4 border-violet-400">
                <div className="w-2 h-2 rounded-full bg-violet-500 mt-2"></div>
                <p className="text-slate-700 flex-1">{element.text}</p>
              </div>
            );
          } else if (element.type === 'connection') {
            return (
              <div key={idx} className="text-xs text-slate-500 px-4 py-2 bg-slate-100 rounded-full border border-dashed border-slate-300">
                {element.text}
              </div>
            );
          } else {
            return (
              <div key={idx} className="w-full max-w-xl">
                <div className="px-6 py-4 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl shadow-lg text-center font-semibold">
                  {element.text}
                </div>
              </div>
            );
          }
        })}
      </div>
    </div>
  );
};

export default DiagramRenderer;
